import { forwardRef } from 'react';
import { connect } from 'react-redux';
import { Button, CircleButton, Tag } from 'tedooo-web-design-system/ui';
import { ExternalLinkIcon, MoreVertical, MoveIcon } from 'tedooo-web-design-system/icons';

import CircleLikeButton from 'components/circleLikeButton/CircleLikeButton';
import { ICatalogItemProps, IStore2, MenuOptions } from 'types';
import { Image, Span } from 'components/helpers';
import './catalogItem.scss';
import { toCurrency } from 'utils';
import { superAdminDeleteProduct } from 'store/searchResult/SearchResultActions';
import DropdownOptionsMenu from 'components/dropdownOptionsMenu/DropdownOptionsMenu';
import TotalSavesTag from 'components/totalSavesTag/TotalSavesTag';
import { CATALOG_ITEM_DROPDOWN_OPTIONS } from '../helper';
import { translations } from 'translations/en';

export const CatalogItem = forwardRef<any, ICatalogItemProps>(
  (
    {
      index,
      id,
      image,
      quantity,
      shippingInformation,
      isDigital,
      name,
      currencyCode,
      price,
      originalPrice,
      isShopOwner,
      totalLikes,
      isLiked,
      isDraggable,
      isSuperAdmin,
      onLikeToggle,
      onClick,
      onDeleteClick,
      onEditClick,
      onDuplicateClick,
      onAdminDeleteClick,
    },
    ref,
  ) => {
    const shouldDisplayPrice = !!(price && currencyCode);

    const displayedName = name || '-';

    const shouldDisplayDot = !!(price && name);

    const shouldDisplayName = name || !price;

    const onCatalogItemClick = () => {
      onClick(index || 0);
    };

    const onSuperAdminDeleteClick = (e: any) => {
      e.stopPropagation();
      onAdminDeleteClick({ productId: id });
    };

    const shouldDisplayShippingInformationError = !shippingInformation && isShopOwner && !isDigital;

    const onOptionClick = (option: MenuOptions) => {
      if (option === MenuOptions.Delete) {
        onDeleteClick();
      }
      if (option === MenuOptions.Edit) {
        onEditClick();
      }
      if (option === MenuOptions.Duplicate) {
        onDuplicateClick();
      }
    };

    const stopPropagation = (e: any) => {
      e.stopPropagation();
    };

    return (
      <div className="catalog-item" onClick={onCatalogItemClick}>
        <div className="image-with-button">
          {totalLikes > 0 && <TotalSavesTag totalLikes={totalLikes} />}
          <Image src={image} withOverlay />
          {!isShopOwner && (
            <Button
              type="secondary"
              size="small"
              borderRadius="large"
              text="Preview"
              className="preview-button"
            />
          )}
          {!isShopOwner && <CircleLikeButton isLiked={isLiked} onClick={onLikeToggle} />}
          {isShopOwner && (
            <div className="buttons">
              {isDraggable && (
                <div ref={ref} className="drag-button" onClick={stopPropagation}>
                  <MoveIcon />
                </div>
              )}
              <div onClick={stopPropagation}>
                <DropdownOptionsMenu
                  onOptionClick={onOptionClick}
                  options={CATALOG_ITEM_DROPDOWN_OPTIONS}
                >
                  <CircleButton icon={<MoreVertical />} type="secondary" size="small" />
                </DropdownOptionsMenu>
              </div>
            </div>
          )}
        </div>
        <div className="details">
          {shouldDisplayPrice && (
            <>
              <Span text={toCurrency(price, currencyCode, 2)} className="price" />
              {originalPrice && originalPrice !== price && (
                <Span
                  text={toCurrency(originalPrice, currencyCode, 2)}
                  className="original-price"
                />
              )}
            </>
          )}
          {shouldDisplayDot && <div className="dot" />}
          {shouldDisplayName && <Span text={displayedName} className="name" />}
        </div>
        {shouldDisplayShippingInformationError && (
          <Button
            type="danger"
            size="small"
            borderRadius="small"
            text="Update shipping info"
            className="update-shipping-info-button"
            suffixIcon={<ExternalLinkIcon />}
            onClick={onEditClick}
          />
        )}
        {quantity === 0 && (
          <Tag color="red" size="small" text={translations.SPECIFIC_PRODUCT.OUT_OF_STOCK} />
        )}
        {isSuperAdmin && (
          <Button
            text="Admin Delete"
            type="danger"
            borderRadius="small"
            size="small"
            onClick={onSuperAdminDeleteClick}
          />
        )}
      </div>
    );
  },
);

const mapDispatchToProps = {
  onAdminDeleteClick: superAdminDeleteProduct,
};

const mapStateToProps = (state: IStore2) => {
  const { isSuperAdmin } = state.user;

  return { isSuperAdmin };
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  CatalogItem,
);
